import React, { useState, useEffect } from "react";
import { Typography, Row, Col, Image, Breadcrumb } from "antd";
import { useParams, Link } from "react-router-dom";
import "./Mercaway.css";
import TechAdvantages from "./techAdvantages/TechAdvantages";
import FlowDiagram from "./flowDiagram/FlowDiagram";

const { Title, Paragraph } = Typography;

const Mercaway = () => {
  const { section } = useParams<{ section?: string }>();
  const [selected, setSelected] = useState<string>("main");

  useEffect(() => {
    if (section === "techadvantages") {
      setSelected("tech");
    } else if (section === "flowdiagram") {
      setSelected("flow");
    } else {
      setSelected("main");
    }
  }, [section]);

  // Helper to build correct URLs for navigation
  const getSectionUrl = (section: string) =>
    section === "main"
      ? "/newBusinessDevelopment/mercaway"
      : section === "tech"
        ? "/newBusinessDevelopment/mercaway/techadvantages"
        : section === "flow"
          ? "/newBusinessDevelopment/mercaway/flowdiagram"
          : "/newBusinessDevelopment/mercaway";

  // Build breadcrumb path based on selected section
  const breadcrumbPath = [
    { key: "home", label: "Home", url: "/" },
    { key: "nbd", label: "new Business Development", url: "/newBusinessDevelopment" },
    { key: "mercaway", label: "MERCAWAY℠", url: getSectionUrl("main") },
  ];
  if (selected === "tech") {
    breadcrumbPath.push({ key: "tech", label: "tech advantages", url: getSectionUrl("tech") });
  } else if (selected === "flow") {
    breadcrumbPath.push({ key: "flow", label: "flow diagram", url: getSectionUrl("flow") });
  }

  const renderContent = () => {
    switch (selected) {
      case "main":
        return (
          <div>
            <Title level={1}>
              Licensing MERCAWAY Process Technology
            </Title>
            <Paragraph className="common-paragraph">
              Chevron's Technology Marketing invites you to contact our New Business Development group to
              learn about licensing our MERCAWAY&#8480; process technology as well as MERCAWAY technical
              services provided to our licensees.
            </Paragraph>
            <Title level={4}>
              Mercury in crude oil and condensate can be effectively reduced by up to 98%
            </Title>
            <Paragraph className="common-paragraph">
              MERCAWAY process technology is a straightforward process for reducing mercury (Hg) content
              in crude oil and condensate using easily understood chemistry and well-proven unit
              operations.
            </Paragraph>
            <Paragraph className="common-paragraph">
              There are four key steps in the MERCAWAY processing system as follows:
            </Paragraph>
            <ol className="Mercaway-list">
              <li>Desanding to remove sand and other particles</li>
              <li>Reaction to convert elemental mercury to particulate HgS</li>
              <li>Automated filtration to separate HgS particles from treated crude/condensate stream</li>
              <li>Polishing to adsorb most of the remaining mercury after the filtration step</li>
            </ol>
            <Row gutter={[16, 16]} className="related-content-promo">
              <Col xs={24} sm={24} md={12} lg={12} xl={12} className="related-content-promo-content">
                <Title level={3}>Tech Advantages</Title>
                <div style={{ marginLeft: "12px" }}>
                  <Link to={getSectionUrl("tech")}>
                    Process operates at modest temperature and pressure
                  </Link>
                </div>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} className="related-content-promo-image">
                <Image className="related-content-promo-image"
                  src="/images/mercaway-tech-advantages.png"
                  alt="Tech Advantages"
                  preview={false}
                />
              </Col>
            </Row>
            <Title level={4}>Desanding Step</Title>
            <Paragraph className="common-paragraph">
              The Desanding Step is accomplished using a hydrocyclone to achieve removal of sand and other
              particulates. The Desanding Step reduces the filtration load and improves cycle length.
              Depending on the nature of the produced crude/condensate stream, this step may be omitted.
            </Paragraph>
            <Title level={4}>Reaction Step</Title>
            <Paragraph className="common-paragraph">
              The Reaction Step is conducted by introducing a treat chemical bearing at least one Hg
              reactive sulfur (S). The treat chemical dosage required can be readily determined using
              laboratory test methods for the crude or condensate to be treated. The reaction takes place
              in a stirred reactor at modest temperature and pressure.
            </Paragraph>
            <Paragraph className="Reaction-step-equation">
              Hg&#176; + S donating compound &rarr; HgS
            </Paragraph>
            <Row gutter={[16, 16]} className="related-content-promo">
              <Col xs={24} sm={24} md={12} lg={12} xl={12} className="related-content-promo-content">
                <Title level={3}>Flow Diagram</Title>
                <div style={{ marginLeft: "12px" }}>
                  <Link to={getSectionUrl("flow")}>
                    License MERCAWAY(SM) technology to reduce mercury in crude or condensate
                  </Link>
                </div>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} className="related-content-promo-image">
                <Image className="related-content-promo-image"
                  src="/images/mercaway-flow-diagram.png"
                  alt="Flow Diagram"
                  preview={false}
                />
              </Col>
            </Row>
            <Title level={4}>Filtration Step</Title>
            <Paragraph className="common-paragraph">
              The Filtration Step uses a pressurized precoat filtration system with operating cycle
              expected to range from 12&ndash;48 hours depending on feed and other filtration variables.
              In the current application, diatomaceous earth (DE) is added first as a filter precoat and
              later a finer grain DE is added to the feed to achieve filtration to 0.7&mu;. DE addition
              rate is determined during the laboratory testing phase. Filtration has proven to provide 98%
              removal of HgS. The Hg containing filter cake is discharged at the end of every cycle for
              environmentally acceptable disposal.
            </Paragraph>
            <Title level={4}>Polishing Step</Title>
            <Paragraph className="common-paragraph">
              The Polishing Step is conducted in a fixed absorbent bed for further reduction of Hg content
              of the crude. This step may be unnecessary if acceptable levels of Hg removal are achieved
              after the Filtration Step.
            </Paragraph>
            <Row gutter={[16, 16]} justify="center" style={{ textAlign: "center" }}>
              <Col xs={24} sm={16} md={8}>
                <Title level={5}>License Our Technology</Title>
                <Image
                  className="Mercaway-image"
                  src="/images/mercaway-feature-web.jpg"
                  alt="MERCAWAY Feature"
                  preview={false}
                  style={{ width: "100%", maxWidth: "300px", margin: "0 auto" }}
                />
                <Title className="License-text" level={5}>
                  Learn More About Licensing MERCAWAY
                </Title>
                <div>
                  <Paragraph>
                    <a className="License-link" href="mailto:kenny.peinado@chevron.com">
                      get in touch
                    </a>
                  </Paragraph>
                </div>
              </Col>
            </Row>
          </div>
        );
      case "tech":
        return <TechAdvantages />;
      case "flow":
        return <FlowDiagram />;
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Breadcrumb with ">" separator and custom style */}
      <div className="breadcrumb">
        <Breadcrumb separator=">">
          {breadcrumbPath.map((item, idx) => (
            <Breadcrumb.Item key={item.key}>
              {idx !== breadcrumbPath.length - 1 ? (
                <Link to={item.url}>{item.label}</Link>
              ) : (
                <span>{item.label}</span>
              )}
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>
      <Row gutter={16}>
        <Col xs={24} sm={24} md={7} lg={6} xl={5}>
          <div className="div-container">
            <ul title="Mercaway">
              <li className="Mercway-links">
                <Link
                  to={getSectionUrl("main")}
                  style={{ color: "#0b2d71", fontWeight: selected === "main" ? "bold" : "bold" }}
                >
                  MERCAWAY℠
                </Link>
              </li>
              <li className="links">
                <Link
                  to={getSectionUrl("tech")}
                  style={{ color: "#0b2d71", fontWeight: selected === "tech" ? "bold" : "normal" }}
                >
                  tech advantages
                </Link>
              </li>
              <li className="links">
                <Link
                  to={getSectionUrl("flow")}
                  style={{ color: "#0b2d71", fontWeight: selected === "flow" ? "bold" : "normal" }}
                >
                  flow diagram
                </Link>
              </li>
            </ul>
          </div>
        </Col>
        <Col xs={24} sm={24} md={16} lg={17} xl={18} offset={0}>
          {renderContent()}
        </Col>
      </Row>
    </div>
  );
};

export default Mercaway;